/**
 * Design System - Modal Component
 * Modale standardizzata per form e contenuti
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Button } from '../../atoms/Button';

export type ModalSize = 'small' | 'medium' | 'large' | 'fullscreen';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  maxWidth?: string;
  fullWidth?: boolean;
}

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
}));

const getModalSize = (size: ModalSize) => {
  switch (size) {
    case 'small':
      return { maxWidth: '400px', width: '90vw' };
    case 'medium':
      return { maxWidth: '600px', width: '90vw' };
    case 'large':
      return { maxWidth: '800px', width: '90vw' };
    case 'fullscreen':
      return { maxWidth: '100vw', width: '100vw', height: '100vh' };
    default:
      return { maxWidth: '600px', width: '90vw' };
  }
};

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  size = 'medium',
  showCloseButton = true,
  maxWidth,
  fullWidth = false,
}) => {
  const modalSize = getModalSize(size);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          ...modalSize,
          maxWidth: maxWidth || modalSize.maxWidth,
        },
      }}
    >
      {(title || showCloseButton) && (
        <StyledDialogTitle>
          <Box>
            {title && (
              <Typography variant="h6" component="h2" fontWeight={600}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {showCloseButton && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ ml: 2 }}
              aria-label="Chiudi"
            >
              <Close />
            </IconButton>
          )}
        </StyledDialogTitle>
      )}

      <StyledDialogContent>
        {children}
      </StyledDialogContent>

      {actions && (
        <StyledDialogActions>
          {actions}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

// Componente helper per azioni standardizzate
export interface ModalActionsProps {
  onCancel: () => void;
  onConfirm?: () => void;
  onSave?: () => void;
  cancelText?: string;
  confirmText?: string;
  saveText?: string;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'confirm' | 'save' | 'custom';
}

export const ModalActions: React.FC<ModalActionsProps> = ({
  onCancel,
  onConfirm,
  onSave,
  cancelText = 'Annulla',
  confirmText = 'Conferma',
  saveText = 'Salva',
  loading = false,
  disabled = false,
  variant = 'confirm',
}) => {
  if (variant === 'custom') {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        onClick={onCancel}
        disabled={loading || disabled}
      >
        {cancelText}
      </Button>
      
      {variant === 'confirm' && onConfirm && (
        <Button
          variant="primary"
          onClick={onConfirm}
          loading={loading}
          disabled={disabled}
        >
          {confirmText}
        </Button>
      )}
      
      {variant === 'save' && onSave && (
        <Button
          variant="primary"
          onClick={onSave}
          loading={loading}
          disabled={disabled}
        >
          {saveText}
        </Button>
      )}
    </>
  );
};

export default Modal;
